import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ShieldCheck, Download, CheckCircle2, MinusCircle, XCircle, ChevronDown } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A' };
const STATUS = {
  implemented: { c: C.green, Icon: CheckCircle2, label: 'Implemented' },
  partial: { c: C.amber, Icon: MinusCircle, label: 'Partial' },
  gap: { c: C.red, Icon: XCircle, label: 'Gap' },
};
const FRAMEWORKS = [{ id: 'SOC2', label: 'SOC 2 Type II' }, { id: 'CPS230', label: 'APRA CPS 230' }];

export default function AuditReadinessPage() {
  const api = useApi();
  const [fw, setFw] = useState('SOC2');
  const [summary, setSummary] = useState(null);
  const [controls, setControls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openFam, setOpenFam] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    return Promise.all([api.get(`/compliance/readiness/overview?framework=${fw}`), api.get(`/compliance/readiness/controls?framework=${fw}`)])
      .then(([o, c]) => { setSummary(o.data.frameworks[fw]); setControls(c.data.controls || []); })
      .catch(e => console.error(e?.message))
      .finally(() => setLoading(false));
  }, [api, fw]);

  useEffect(() => { load(); }, [load]);

  const updateControl = (ctrl, status) => {
    api.put(`/compliance/readiness/controls/${fw}/${ctrl.control_id}`, { status })
      .then(() => { toast.success(`${ctrl.control_id} → ${STATUS[status].label}`); load(); })
      .catch(e => toast.error(e?.response?.data?.detail || 'Update failed'));
  };

  const exportJson = () => {
    api.get(`/compliance/readiness/export?framework=${fw}`).then(r => {
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${fw}_readiness_${new Date().toISOString().slice(0, 10)}.json`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Readiness pack exported');
    }).catch(() => toast.error('Export failed'));
  };

  if (loading && !summary) return <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 rounded-md" />)}</div>;

  const byFamily = controls.reduce((acc, c) => { (acc[c.family] = acc[c.family] || []).push(c); return acc; }, {});
  const ring = summary?.readiness_pct || 0;
  const ringColor = ring >= 75 ? C.green : ring >= 50 ? C.amber : C.red;

  return (
    <div className="space-y-5" data-testid="audit-readiness-page">
      {/* Framework toggle + export */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1.5" data-testid="framework-toggle">
          {FRAMEWORKS.map(f => (
            <button key={f.id} onClick={() => setFw(f.id)} data-testid={`fw-${f.id}`}
              className="px-3.5 py-2 rounded-md text-xs font-semibold transition-all"
              style={fw === f.id ? { background: C.navy, color: 'white' } : { background: 'white', color: C.slate, border: '1px solid rgba(136,150,168,0.2)' }}>
              {f.label}
            </button>
          ))}
        </div>
        <Button size="sm" variant="outline" onClick={exportJson} data-testid="export-btn"><Download className="w-3.5 h-3.5 mr-1.5" />Export Pack</Button>
      </div>

      {/* Readiness hero */}
      {summary && (
        <Card className="p-5 rounded-lg flex flex-col sm:flex-row sm:items-center gap-6" style={{ border: '1px solid rgba(136,150,168,0.14)' }} data-testid="readiness-hero">
          <div className="flex items-center gap-4">
            <div className="relative w-20 h-20 shrink-0">
              <svg viewBox="0 0 36 36" className="w-20 h-20 -rotate-90">
                <circle cx="18" cy="18" r="15.9" fill="none" stroke="#EEF1F5" strokeWidth="3.2" />
                <circle cx="18" cy="18" r="15.9" fill="none" stroke={ringColor} strokeWidth="3.2" strokeDasharray={`${ring} ${100 - ring}`} strokeLinecap="round" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center"><span className="text-lg font-bold font-mono" style={{ color: ringColor }} data-testid="readiness-pct">{ring}%</span></div>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider font-semibold flex items-center gap-1.5" style={{ color: C.blue }}><ShieldCheck className="w-4 h-4" />{summary.name} Readiness</p>
              <p className="text-sm mt-1" style={{ color: C.slate }}>{summary.total_controls} controls tracked</p>
            </div>
          </div>
          <div className="flex gap-6">
            {['implemented', 'partial', 'gap'].map(s => {
              const cfg = STATUS[s];
              return (
                <div key={s} className="flex items-center gap-2" data-testid={`summary-${s}`}>
                  <cfg.Icon className="w-5 h-5" style={{ color: cfg.c }} />
                  <div><div className="text-xl font-bold font-mono" style={{ color: C.navy }}>{summary[s]}</div><div className="text-[11px]" style={{ color: C.slate }}>{cfg.label}</div></div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Families */}
      <div className="space-y-3">
        {Object.entries(byFamily).map(([family, items]) => {
          const fam = summary?.families?.find(f => f.family === family);
          const open = openFam === family;
          return (
            <Card key={family} className="rounded-md overflow-hidden" style={{ border: '1px solid rgba(136,150,168,0.14)' }} data-testid={`family-${family.replace(/\s+/g, '-')}`}>
              <button onClick={() => setOpenFam(open ? null : family)} className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left">
                <div className="flex items-center gap-3 min-w-0">
                  <ChevronDown className="w-4 h-4 shrink-0 transition-transform" style={{ color: C.slate, transform: open ? 'rotate(0)' : 'rotate(-90deg)' }} />
                  <span className="text-sm font-semibold" style={{ color: C.navy }}>{family}</span>
                  <span className="text-xs" style={{ color: C.slate }}>{items.length} controls</span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="w-28 h-1.5 rounded-full" style={{ background: '#EEF1F5' }}>
                    <div className="h-1.5 rounded-full" style={{ width: `${fam?.readiness_pct || 0}%`, background: (fam?.readiness_pct || 0) >= 75 ? C.green : (fam?.readiness_pct || 0) >= 50 ? C.amber : C.red }} />
                  </div>
                  <span className="text-xs font-mono font-semibold w-10 text-right" style={{ color: C.navy }}>{fam?.readiness_pct || 0}%</span>
                </div>
              </button>
              {open && (
                <div className="border-t" style={{ borderColor: 'rgba(136,150,168,0.1)' }}>
                  {items.map(ctrl => {
                    const cfg = STATUS[ctrl.status];
                    return (
                      <div key={ctrl.control_id} className="px-4 py-3 border-t flex items-start justify-between gap-4" style={{ borderColor: 'rgba(136,150,168,0.06)' }} data-testid={`control-${ctrl.control_id}`}>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono font-semibold" style={{ color: C.blue }}>{ctrl.control_id}</span>
                            <span className="text-sm font-medium" style={{ color: C.navy }}>{ctrl.title}</span>
                          </div>
                          <p className="text-[11px] mt-0.5" style={{ color: C.slate }}>{ctrl.evidence}</p>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          {['implemented', 'partial', 'gap'].map(s => {
                            const sc = STATUS[s];
                            const active = ctrl.status === s;
                            return (
                              <button key={s} onClick={() => updateControl(ctrl, s)} data-testid={`set-${ctrl.control_id}-${s}`} title={sc.label}
                                className="w-7 h-7 rounded flex items-center justify-center transition-all"
                                style={{ background: active ? `${sc.c}1A` : 'transparent', border: `1px solid ${active ? sc.c : 'rgba(136,150,168,0.2)'}` }}>
                                <sc.Icon className="w-3.5 h-3.5" style={{ color: active ? sc.c : 'rgba(136,150,168,0.5)' }} />
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
